/**
 * VÉRAULT Member Suite — Experiences (Phase 1a · foundation)
 * ─────────────────────────────────────────────────────────────
 * Replaces "Trips" / "Itineraries" — every trip is now a private
 * editorial microsite (an "Experience"). This page is the gallery
 * that opens the door to each one.
 *
 * Phase 1a builds the editorial card grid + section grouping
 * (Current · Upcoming · Past). Phase 1b will plug in the hidden-
 * destination veil so masked experiences render with atmosphere
 * imagery + the "Secret Destination" label.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass } from "lucide-react";
import {
  Heading,
  EditorialRule,
  Empty,
  api,
  motion as portalMotion,
} from "../../index";
import { useDestinationVeil } from "../../../hooks/useDestinationVeil";

const { fadeInUp, stagger } = portalMotion;

// ─── Card ────────────────────────────────────────────────────────
function ExperienceCard({ trip, priority }) {
  const veil = useDestinationVeil(trip);
  const title = veil.label;
  const dateRange = veil.dateLabel;
  const image = veil.image;

  return (
    <Link
      to={`/portal/client/experiences/${trip.id}`}
      className="group block isolate"
      data-testid={`member-experience-card-${trip.id}`}
      data-veiled={veil.veiled ? "true" : "false"}
    >
      <div className="relative overflow-hidden rounded-md bg-[hsl(var(--background-stone))]">
        <div className="aspect-[4/5] sm:aspect-[3/4] relative">
          {image ? (
            <img
              src={image}
              alt={title}
              loading={priority ? "eager" : "lazy"}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] group-hover:scale-[1.04]"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--background-paper))] via-[hsl(var(--background-stone))] to-[hsl(var(--background-paper))]" />
          )}
          {/* Atmospheric trips get a gentler, more diffused overlay so the
              imagery reads as mood, not place. */}
          <div className={`absolute inset-0 ${veil.atmosphere
            ? "bg-gradient-to-t from-black/65 via-black/20 to-black/5"
            : "bg-gradient-to-t from-black/55 via-black/10 to-transparent"}`}
          />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-white">
          <p className="font-mono text-[10px] uppercase tracking-[0.26em] opacity-80">
            {veil.eyebrow}
          </p>
          <h3 className="mt-1.5 font-serif text-[22px] sm:text-[26px] leading-tight tracking-tight">
            {title}
          </h3>
          <p className="mt-1.5 font-accent italic text-[13.5px] opacity-90">
            {dateRange}
          </p>
        </div>
      </div>
    </Link>
  );
}

// ─── Section group ───────────────────────────────────────────────
function SectionGroup({ label, trips, priority }) {
  if (!trips || trips.length === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
      className="space-y-5"
      data-testid={`member-experience-section-${label.toLowerCase()}`}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          {label}
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
          {trips.length} {trips.length === 1 ? "experience" : "experiences"}
        </span>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
        {trips.map((t) => (
          <li key={t.id}>
            <ExperienceCard trip={t} priority={priority} />
          </li>
        ))}
      </ul>
    </motion.section>
  );
}

// ─── Page ────────────────────────────────────────────────────────
export default function MemberExperiences() {
  const [trips, setTrips] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    api.get("/client-portal/trips")
      .then(({ data }) => alive && setTrips(Array.isArray(data) ? data : []))
      .catch(() => alive && setError("Couldn't load your experiences."));
    return () => { alive = false; };
  }, []);

  // Group into Current / Upcoming / Past based on date.
  const { current, upcoming, past } = React.useMemo(() => {
    if (!trips) return { current: [], upcoming: [], past: [] };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const c = []; const u = []; const p = [];
    for (const t of trips) {
      const startStr = t.trip_start_date || t.start_date;
      const endStr = t.trip_end_date || t.end_date;
      const start = startStr ? new Date(startStr) : null;
      const end = endStr ? new Date(endStr) : null;
      if (start && end && start <= today && end >= today) c.push(t);
      else if (start && start > today) u.push(t);
      else if (end && end < today) p.push(t);
      else u.push(t); // undated → treat as upcoming
    }
    u.sort((a, b) => new Date(a.trip_start_date || a.start_date || 0) - new Date(b.trip_start_date || b.start_date || 0));
    p.sort((a, b) => new Date(b.trip_end_date || b.end_date || 0) - new Date(a.trip_end_date || a.end_date || 0));
    return { current: c, upcoming: u, past: p };
  }, [trips]);

  const totalCount = trips?.length || 0;

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-12 sm:space-y-16 pb-12"
      data-testid="member-experiences-root"
    >
      <motion.div variants={fadeInUp}>
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--foreground-muted))]">
          Member Suite
        </p>
        <Heading level="display" className="mt-3 !text-[40px] sm:!text-[52px] lg:!text-[64px] !leading-[1.05]">
          Experiences.
        </Heading>
        <p className="mt-4 font-accent italic text-[16px] sm:text-[18px] text-[hsl(var(--foreground-secondary))] max-w-xl leading-relaxed">
          A quiet shelf for every journey — the ones you're inside, the ones
          on the horizon, the ones you've already lived.
        </p>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <EditorialRule label={trips ? `${totalCount} ${totalCount === 1 ? "experience" : "experiences"}` : "loading"} />
      </motion.div>

      {error ? (
        <Empty title={error} icon={<Compass className="size-6" strokeWidth={1.25} />} />
      ) : trips === null ? (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
          {[0, 1, 2].map((i) => (
            <li key={i} className="aspect-[4/5] sm:aspect-[3/4] rounded-md bg-[hsl(var(--background-paper))]/60 animate-pulse" />
          ))}
        </ul>
      ) : trips.length === 0 ? (
        <Empty
          title="Your shelf is quiet for now."
          hint="When your advisor opens your next experience, it will rest here, fully composed."
          icon={<Compass className="size-6" strokeWidth={1.25} />}
        />
      ) : (
        <>
          <SectionGroup label="Now" trips={current} priority />
          <SectionGroup label="Upcoming" trips={upcoming} priority={current.length === 0} />
          <SectionGroup label="Past" trips={past} />
        </>
      )}
    </motion.div>
  );
}
